/* eslint-disable max-len */
import React, {useEffect, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useParams} from 'react-router-dom';
import styled from 'styled-components';
import ChapterFooter from '../components/Comics/ChapterFooter';
import ChapterSpoilerView from '../components/Comics/ChapterSpoilerView';
import ReaderCollapsableHeader from '../components/Comics/ComicsCollapsableHeader';
import ComicsTutorial from '../components/Comics/ComicsTutorial';
import ReaderCollapsableFooter from '../components/Comics/ReaderCollapsableFooter';
import ButtonToTop from '../components/Comics/TopButton';
import Loader from '../components/Loader';
import {
    comicTutorialClose,
    loadComics,
    updateProgress,
} from '../store/actions/comics';
import {
    selectComicByFriendlyName,
    selectComics,
    selectDatedProgressForChapter,
    selectCurrentChapter,
    selectShouldShowComicsTutorial,
    selectChapterRead,
} from '../store/reducers';
import {notWebMediaQuery, useIsWeb} from '../styles';
import {getCurrentLang} from '../utils/i18n';

const ComicsChapterReader = () => {
    const dispatch = useDispatch();
    const loadedComics = useSelector(selectComics);
    const [collapsed, setCollapsed] = useState(false);

    const [padding, setPadding] = useState(215);
    const [showSpoiler, setShowSpoiler] = useState(false);
    const params = useParams();
    const shouldShowTutorial = useSelector(selectShouldShowComicsTutorial);
    const [showTutorial, setShowTutorial] = useState(shouldShowTutorial);
    let comicId = params.comic_id;
    let chapterNumber = params.chapter;
    let comic = useSelector(state => selectComicByFriendlyName(state, comicId));
    let chapter = comic?.chapters[chapterNumber - 1];
    let expectedChapter = useSelector(
        state => selectCurrentChapter(state, comic) || comic?.chapters[0],
    );
    const scrollContent = useRef(null);
    let savedProgress =
        useSelector(state =>
            selectDatedProgressForChapter(state, chapter?.id || 0),
        )?.progress || 0;
    const [progress, setProgress] = useState(savedProgress);
    const isRead = useSelector(state => selectChapterRead(state, chapter?.id));
    const [canTrackCompletion, setCanTrackCompletion] = useState(false);
    const user = useSelector(state => state.user);
    const idp = user && user.idp;
    const [sentPageview, setSentPageview] = useState(false);
    const isWeb = useIsWeb();

    useEffect(() => {
        if (isRead === false) {
            //isRead can be null when page is loading
            setCanTrackCompletion(true);
        }
    }, [isRead]);

    useEffect(() => {
        if (!sentPageview && comic) {
            setSentPageview(true);

            window.turner_metadata.trackAction.push({
                type: 'pageview',
                data: {
                    pageName: 'cnplus.com/comics/chapter',
                    section: 'comics',
                    subsection: 'comics',
                    contenttype: 'comics',
                    contenttitle: comic.title,
                    presentationtemplate: 'desktop',
                    platform: 'web',
                    domain: 'cnplus.com',
                    region: 'latam',
                    country:
                        user && user.countryIsoCode && user.countryName
                            ? `${user.countryIsoCode.toLowerCase()}|${
                                  user.countryName
                              }`
                            : 'not loaded',
                    brand: 'cartoon network',
                    pageviewevent: 1,
                    authstate:
                        user && user.jwtToken
                            ? 'authenticated'
                            : 'unauthenticated',
                    mvpd: idp || 'idp not loaded',
                    userlang: getCurrentLang(),
                    franchise: comic.show,
                    chapter_number: String(chapter.number),
                },
            });
        }

        // eslint-disable-next-line
    }, [comic]);

    useEffect(() => {
        //make sure this is only tracked once
        if (isRead && canTrackCompletion) {
            window.turner_metadata.trackAction.push({
                type: 'interaction',
                data: {
                    interaction: 'comic|complete',
                    domain: 'cnplus.com',
                    brand: 'cartoon network',
                    server: 'cnplus',
                    platform: 'web',
                    appname: 'app|cnplus|web',
                    region: 'latam',
                    country:
                        user && user.countryIsoCode && user.countryName
                            ? `${user.countryIsoCode.toLowerCase()}|${
                                  user.countryName
                              }`
                            : 'not loaded',
                    interactionevent: '1',
                    authstate:
                        user && user.jwtToken
                            ? 'authenticated'
                            : 'unauthenticated',
                    mvpd: idp || 'idp not loaded',
                    userlang: getCurrentLang(),
                    franchise: comic.show,
                    contenttype: 'comics',
                    contenttitle: comic.title,
                    progress: '10',
                    chapter_number: String(chapter.number),
                    chapter_total: String(comic.chapters.length),
                    pages_total: '10',
                    english_name: comic.englishTitle.toLowerCase(),
                    comiccomplete_event: '1',
                },
            });
            setCanTrackCompletion(false);
        }
    }, [isRead, chapter, comic, canTrackCompletion, idp, user]);

    //analytics functions
    const trackEnterState = (_progress, _comic, _chapter) => {
        let extra = {};
        if (_progress === 0) {
            extra.interaction = 'comic|start';
            extra.comicstart_event = '1';
        } else {
            extra.interaction = 'comic|continue';
            extra.comiccontinue_event = '1';
        }

        window.turner_metadata.trackAction.push({
            type: 'interaction',
            data: {
                ...extra,
                domain: 'cnplus.com',
                brand: 'cartoon network',
                server: 'cnplus',
                platform: 'web',
                region: 'latam',
                country:
                    user && user.countryIsoCode && user.countryName
                        ? `${user.countryIsoCode.toLowerCase()}|${
                              user.countryName
                          }`
                        : 'not loaded',
                interactionevent: '1',
                authstate:
                    user && user.jwtToken ? 'authenticated' : 'unauthenticated',
                mvpd: idp || 'idp not loaded',
                userlang: getCurrentLang(),
                franchise: _comic.show,
                contenttype: 'comics',
                contenttitle: _comic.title,
                progress: String(
                    Math.min(10, Math.round((_progress * 100) / 10)),
                ),
                chapter_number: String(_chapter.number),
                chapter_total: String(_comic.chapters.length),
                pages_total: '10',
                english_name: _comic.englishTitle.toLowerCase(),
            },
        });
    };

    useEffect(() => {
        if (!loadedComics) {
            dispatch(loadComics());
        }
    }, [dispatch, loadedComics]);

    const [offset, setOffset] = useState(0);
    const [lastOffset, setLastOffset] = useState(0);

    useEffect(() => {
        if (chapter == null) {
            return;
        }
        if (progress < 1) {
            let width = Math.min(window.innerWidth, 1024);
            let offset =
                ((width * 800) / 1024) * savedProgress * chapter?.images.length;
            scrollContent.current.scrollTo(0, offset);
        }

        //check if this is the last chapter read, otherwise warn about spoilers
        if (savedProgress === 0 && expectedChapter?.number < chapter?.number) {
            setShowSpoiler(true);
        }

        trackEnterState(progress, comic, chapter);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chapter]);

    useEffect(() => {
        handleWindowResize();

        window.addEventListener('resize', handleWindowResize);

        return () => {
            window.removeEventListener('resize', handleWindowResize);
        };
        // eslint-disable-next-line
    }, []);

    const [windowHeight, setWindowHeight] = useState(window.innerHeight);

    const handleWindowResize = () => {
        setWindowHeight(window.innerHeight);
    };

    const onScroll = () => {
        setPadding(90);
        if (scrollContent.current) {
            setOffset(Math.max(0, scrollContent.current?.scrollTop));
            //ignore progress if the images are still loading
            if (scrollContent.current.scrollHeight > window.innerHeight) {
                setProgress(
                    scrollContent.current.scrollTop /
                        (scrollContent.current.scrollHeight -
                            window.innerHeight -
                            600), //600 for the footer and to give some slack
                );
            }
        }
    };

    useEffect(() => {
        if (offset > lastOffset) {
            setCollapsed(true);
        } else if (
            (offset < lastOffset && !isWeb) ||
            (offset < lastOffset &&
                offset <
                    scrollContent.current?.scrollHeight -
                        window.innerHeight -
                        600 &&
                isWeb)
        ) {
            setCollapsed(false);
        }
        setLastOffset(offset);
    }, [offset, lastOffset, isWeb]);

    useEffect(() => {
        if (chapter) {
            dispatch(updateProgress(chapter.id, progress));
        }
    }, [progress, dispatch, chapter]);

    const scrollToTop = () => {
        scrollContent.current.scrollTo(0, 0);
    };

    const dismissSpoiler = () => {
        setShowSpoiler(false);

        window.turner_metadata.trackAction.push({
            type: 'interaction',
            data: {
                interaction: 'comic|spoiler alert chapter',
                domain: 'cnplus.com',
                brand: 'cartoon network',
                server: 'cnplus',
                platform: 'web',
                region: 'latam',
                country:
                    user && user.countryIsoCode && user.countryName
                        ? `${user.countryIsoCode.toLowerCase()}|${
                              user.countryName
                          }`
                        : 'not loaded',
                interactionevent: '1',
                authstate:
                    user && user.jwtToken ? 'authenticated' : 'unauthenticated',
                mvpd: idp || 'idp not loaded',
                userlang: getCurrentLang(),
                franchise: comic.show,
                contenttype: 'comics',
                contenttitle: comic.title.toLowerCase(),
                english_name: comic.englishTitle.toLowerCase(),
                chapter_number: String(chapter.number),
                spoileralert_event: '1',
            },
        });
    };

    const onDismiss = () => {
        setShowTutorial(false);
        dispatch(comicTutorialClose());
    };

    if (!comic) {
        return (
            <Container>
                <Loader dark />
            </Container>
        );
    }

    return (
        <Container>
            <ReaderCollapsableHeader
                collapsed={collapsed}
                comic={comic}
                chapter={chapter}
                progress={progress}
            />
            <Scrollable
                height={windowHeight}
                onScroll={onScroll}
                ref={scrollContent}
            >
                <Content padding={padding}>
                    {chapter.images.map((image, index) => (
                        <Image
                            key={index}
                            src={`${comic.baseUrl}/${chapter.folder}/${image}`}
                            aspect={
                                index < chapter.images.length - 1
                                    ? 1024 / 800
                                    : undefined
                            }
                        />
                    ))}
                    <ChapterFooter comic={comic} chapter={chapter} />
                </Content>
            </Scrollable>
            <ComicsTutorial
                visible={showTutorial && !showSpoiler}
                onDismiss={onDismiss}
            />
            <ButtonToTop
                onPress={scrollToTop}
                visible={collapsed || (showTutorial && !showSpoiler)}
            />
            {!isWeb && (
                <ReaderCollapsableFooter
                    comic={comic}
                    chapter={chapter}
                    collapsed={collapsed}
                />
            )}
            {showSpoiler && (
                <ChapterSpoilerView
                    opened={chapter}
                    correct={expectedChapter}
                    onDismiss={dismissSpoiler}
                />
            )}
        </Container>
    );
};

const Container = styled.div`
    display: flex;
    width: 100%;
    height: 100%;
    align-self: center;
    flex-direction: column;
    position: relative;
    overflow: hidden;
    ${notWebMediaQuery} {
        margin-bottom: -64px;
    }
`;

const Content = styled.div`
    display: flex;
    width: 100%;
    flex-direction: column;
    padding-top: ${props => props.padding}px;
`;

const Scrollable = styled.div`
    overflow-y: scroll;
    width: 100%;
    height: ${props => props.height}px;
    ::-webkit-scrollbar-thumb {
        background: #840000;
        border: 1px solid #802113;
        border-radius: 50px;
    }
`;

const Image = styled.img`
    width: 100%;
    max-width: 1024px;
    aspect-ratio: ${props => props.aspect};
    align-self: center;
`;

export default ComicsChapterReader;
